import { Range, Root, Thumb, Track } from '@radix-ui/react-slider';
import useEmblaCarousel, { EmblaOptionsType } from 'embla-carousel-react';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { Icon } from '@/components/global/Icons';
import classNames from '@/lib/classNames';
import cn from '@/lib/classNames';
import ClassNames from 'embla-carousel-class-names';

interface SlideProps {
    children: React.ReactNode;
    className?: string;
}

const Slides = forwardRef(({ children, className }: SlideProps, ref) => (
    <div ref={ref as React.RefObject<HTMLDivElement>} className={cn('overflow-hidden embla', className)}>
        <div className={cn('flex embla__container')}>{[children].flat().map((child) => child)}</div>
    </div>
));
Slides.displayName = 'Slides';

interface ProgressBarProps {
    scrollTo: (index: number) => void;
    max: number;
    centered?: boolean;
    scrollProgress: number;
    scrollPrev: () => void;
    scrollNext: () => void;
}

const ProgressBar = ({ scrollTo, max, centered, scrollProgress, scrollPrev, scrollNext }: ProgressBarProps) => {
    const [value, setValue] = useState(centered ? 50 : scrollProgress * 100);
    const [isDragging, setIsDragging] = useState(false);

    /**
     * We need to update the value when the scrollProgress changes
     * so as a user scrolls the carousel itself we want to update the value of the progress bar
     */
    useEffect(() => {
        // We need to check we're not dragging the slider before updating the value
        // as we handle setting the value in the handleValueChange function
        if (!isDragging) {
            setValue(scrollProgress * 100);
        }
    }, [scrollProgress, isDragging]);

    /**
     * We need to set the value here based on the actual slider thumb position
     * scrollProgress causes the slider to jump around when the user is dragging
     */
    const handleValueChange = (newValue: number[]) => {
        // We need to set the value here based on the actual slider thumb position
        setValue(newValue[0]);

        // Get the position of the slider and convert it to a slide index
        const newPosition = Math.round((newValue[0] / 100) * (max - 1));
        // Now scroll to that index
        scrollTo(newPosition);
    };

    /**
     * We need to set the isDragging state to true when the user starts dragging
     */
    const handleDragStart = () => {
        setIsDragging(true);
    };

    /**
     * We need to set the isDragging state to false when the user stops dragging
     */
    const handleDragEnd = () => {
        setIsDragging(false);
    };

    return (
        <div className="w-full justify-between items-center mt-8 hidden lg:flex">
            <div className="group mx-16">
                <button type="button" onClick={scrollPrev} className="left-2 bottom-1/2 bg-white border-primary p-2 hover:bg-primary z-[50]">
                    <Icon id="chevron-left" className="w-6 h-6 fill-primary group-hover:fill-white" />
                </button>
            </div>
            <Root className="relative flex items-center select-none touch-none w-full h-5" value={[value]} max={100} min={0} step={1} onValueChange={handleValueChange}>
                <Track className="bg-shell relative grow rounded-full h-[5px]">
                    <Range className="absolute bg-shell rounded-full h-full" />
                </Track>
                <Thumb
                    className={classNames('block w-32 h-[5px] rounded-full bg-primary text-center cursor-grab', isDragging && 'cursor-grabbing')}
                    aria-label="Slide"
                    onPointerDown={handleDragStart}
                    onPointerUp={handleDragEnd}
                >
                    <span className="text-rock pt-2 lg:pt-3 h6 mt-1 inline-block">Slide</span>
                </Thumb>
            </Root>
            <div className="group mx-16">
                <button type="button" onClick={scrollNext} className="right-2 bottom-1/2 bg-white  border-primary p-2 hover:bg-primary z-[50]">
                    <Icon id="chevron-right" className="w-6 h-6 fill-primary group-hover:fill-white" />
                </button>
            </div>
        </div>
    );
};
ProgressBar.displayName = 'ProgressBar';

type EmblaCarouselChildrenProps = {
    emblaRef: <ViewportElement extends HTMLElement>(instance: ViewportElement | null) => void;
    currentIndex: number;
    setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
    scrollPrev: () => void;
    scrollNext: () => void;
    scrollTo: (index: number) => void;
    scrollProgress: number;
    tweenValues: number[];
    scrollSnaps: number[];
};

interface Props {
    children: (props: EmblaCarouselChildrenProps) => React.ReactNode;
    emblaOptions?: EmblaOptionsType;
    tweenFactor?: number;
    enabled?: boolean;
}

const numberWithinRange = (number: number, min: number, max: number): number => Math.min(Math.max(number, min), max);

const EmblaCarousel = ({ children, emblaOptions, tweenFactor = 0.25 }: Props) => {
    const [emblaRef, embla] = useEmblaCarousel(emblaOptions, [ClassNames()]);

    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [tweenValues, setTweenValues] = useState<number[]>([]);

    const scrollPrev = useCallback(() => {
        embla && embla.scrollPrev();
    }, [embla]);

    const scrollNext = useCallback(() => {
        embla && embla.scrollNext();
    }, [embla]);

    const scrollTo = useCallback((index: number) => embla && embla.scrollTo(index), [embla]);

    const getScrollProgress = useCallback(() => {
        if (!embla) {
            return;
        }

        const engine = embla.internalEngine();
        const progress = embla.scrollProgress();

        const styles = embla.scrollSnapList().map((scrollSnap, index) => {
            let diffToTarget = scrollSnap - progress;

            if (engine.options.loop) {
                engine.slideLooper.loopPoints.forEach((loopItem) => {
                    const target = loopItem.target();
                    if (index === loopItem.index && target !== 0) {
                        const sign = Math.sign(target);
                        if (sign === -1) {
                            diffToTarget = scrollSnap - (1 + progress);
                        }
                        if (sign === 1) {
                            diffToTarget = scrollSnap + (1 - progress);
                        }
                    }
                });
            }
            const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor);
            return numberWithinRange(tweenValue, 0, 1);
        });

        setScrollProgress(Math.max(0, Math.min(1, progress)));
        setTweenValues(styles);
    }, [embla, setTweenValues, tweenFactor]);

    const getScrollSnap = useCallback(() => {
        if (!embla) {
            return;
        }

        setScrollSnaps(embla.scrollSnapList());
        setCurrentIndex(embla.selectedScrollSnap());
    }, [embla]);

    useEffect(() => {
        if (embla) {
            getScrollSnap();
            getScrollProgress();
            embla.on('select', getScrollSnap);
            embla.on('scroll', getScrollProgress);
            embla.on('reInit', getScrollProgress);
        }
    }, [embla, getScrollSnap, getScrollProgress]);

    return children
        ? children({
              emblaRef,
              currentIndex,
              setCurrentIndex,
              scrollPrev,
              scrollNext,
              scrollTo,
              scrollProgress,
              tweenValues,
              scrollSnaps,
          })
        : null;
};

EmblaCarousel.Slides = Slides;
EmblaCarousel.ProgressBar = ProgressBar;

export { EmblaCarousel };
